import {
  Button,
  NumberInput,
  Group,
  Text,
  Title,
  Table,
  SimpleGrid,
  Divider,
  Checkbox,
  Space,
  Card,
  Paper,
} from "@mantine/core";
import React, { useState, useEffect } from "react";
import _ from "lodash";
import DataTable from "./dataTable";

import ReactJson from "react-json-view";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";
import { useForceUpdate } from "@mantine/hooks";
import { IconAlertTriangle } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { OrderFilter } from "../components/orderFilter";
import { PeriodFilter } from "../components/periodFilter";
import { useAuthUser } from "react-auth-kit";
import SimpleLoadingIndicator from "./simpleLoadingIndicator";

const formatOrderCount = (value) => {
  if (value == null || Number.isNaN(Number(value))) return 0;
  const n = Number(value);
  return Number.isInteger(n) ? n : _.round(n, 1);
};

const OrderStatsTable = ({
  data,
  mt = "50px",
  highlightOnHover = true,
  showIndividualConsultant = false,
  remark,
  textSize = "xs",
  onPeriodChange = () => {},
  onPeriodRefresh = () => {},
  onUserChange = () => {},
  onDateTypeChange = () => {},
  onProductCategoryChange = () => {},
  withProductCategoryFilter = false,
  onSelectionChange = () => {},
  periodBy = "Q",
  numQuarters = 10,
  loading = false,
  ...props
}) => {
  const [formatter] = useFormatter();
  const forceUpdate = useForceUpdate();
  const defaultCheckStatus = ["INF", "SUB", "RCV", "DFT", "EXP", "AIP", "AIP_EXP"];
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const auth = useAuthUser();

  const selection = rows.filter((r) => r.checked);

  const notifySelectionChange = (currentRows) => {
    onSelectionChange(
      currentRows.filter((r) => r.checked).map((r) => r._id)
    );
  };

  const toggleAll = () => {
    rows.forEach((r) => (r.checked = !r.checked));
    forceUpdate();
    notifySelectionChange(rows);
  };

  useEffect(() => {
    if (!data) return setRows([]);
    let newRows = data?.sort((a, b) => b.count - a.count);

    newRows.forEach((r) => {
      if (defaultCheckStatus.includes(r._id)) r.checked = true;
    });
    setRows(newRows);
    forceUpdate();
    notifySelectionChange(newRows);
  }, [data]);

  const TableHeader = () => (
    <thead>
      <tr>
        <th>
          <Checkbox
            size={textSize}
            onChange={toggleAll}
            checked={selection?.length === data?.length}
            indeterminate={
              selection.length > 0 && selection.length !== data.length
            }
          />
        </th>
        <th>
          <Text size={textSize}>{t("Status")}</Text>
        </th>
        <th>
          <Text size={textSize}>{t("Count")}</Text>
        </th>
        <th>
          <Text size={textSize}>{t("FY Premium")}</Text>
        </th>
        {!showIndividualConsultant && (
          <th>
            <Text size={textSize}>{t("FY Commission (Total)")}</Text>
          </th>
        )}
        {!showIndividualConsultant && (
          <th>
            <Text size={textSize}>{t("FY Commission (Producers)")}</Text>
          </th>
        )}
        {showIndividualConsultant && (
          <th>
            <Text size={textSize}>{t("FY Commission (Individual)")}</Text>
          </th>
        )}
      </tr>
    </thead>
  );

  const TableRows = ({ data }) => {
    return (
      <tbody>
        {rows?.map((r, index) => (
          <tr key={index}>
            <td>
              <Checkbox
                size={textSize}
                checked={r.checked}
                onChange={(e) => {
                  r.checked = e.target.checked;
                  forceUpdate();
                  notifySelectionChange(rows);
                }}
              ></Checkbox>
            </td>
            <td>
              <Text size={textSize}>{r._id}</Text>
            </td>
            {!showIndividualConsultant && (
              <td>
                <Text size={textSize}>{r.count}</Text>
              </td>
            )}
            {showIndividualConsultant && (
              <td>
                <Text size={textSize}>
                  {formatOrderCount(r.indidvidualOrderCount)}
                </Text>
              </td>
            )}

            <td>
              <Text size={textSize}>{formatter.currency(r.yearPremium)}</Text>
            </td>
            {!showIndividualConsultant && (
              <td>
                <Text size={textSize}>
                  {formatter.currency(r.yearCommission ?? 0)}
                </Text>
              </td>
            )}
            {!showIndividualConsultant && (
              <td>
                <Text size={textSize}>
                  {formatter.currency(r.yearConsultantCommission ?? 0)}
                </Text>
              </td>
            )}
            {showIndividualConsultant && (
              <td>
                <Text size={textSize}>
                  {formatter.currency(r.individualConsultantCommission)}
                </Text>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    );
  };

  const TableFooter = ({ data }) => {
    const checkedRow = rows.filter((r) => r.checked);
    return (
      <tfoot>
        <tr>
          <th></th>
          <th>
            <Text size={textSize} weight="bold">
              Total
            </Text>
          </th>
          {!showIndividualConsultant && (
            <th>
              <Text size={textSize}>
                {checkedRow?.reduce((a, c) => a + c.count, 0)}
              </Text>
            </th>
          )}
          {showIndividualConsultant && (
            <th>
              <Text size={textSize}>
                {formatOrderCount(
                  checkedRow?.reduce((a, c) => a + c.indidvidualOrderCount, 0)
                )}
              </Text>
            </th>
          )}

          <th>
            <Text size={textSize}>
              {formatter.currency(
                checkedRow?.reduce((a, c) => a + c.yearPremium, 0)
              )}
            </Text>
          </th>
          {!showIndividualConsultant && (
            <th>
              <Text size={textSize}>
                {formatter.currency(
                  checkedRow?.reduce(
                    (a, c) => a + (c.yearCommission ?? 0),
                    0
                  )
                )}
              </Text>
            </th>
          )}
          {!showIndividualConsultant && (
            <th>
              <Text size={textSize}>
                {formatter.currency(
                  checkedRow?.reduce(
                    (a, c) => a + (c.yearConsultantCommission ?? 0),
                    0
                  )
                )}
              </Text>
            </th>
          )}

          {showIndividualConsultant && (
            <th>
              <Text size={textSize}>
                {formatter.currency(
                  checkedRow?.reduce(
                    (a, c) => a + c.individualConsultantCommission,
                    0
                  )
                )}
              </Text>
            </th>
          )}
        </tr>
      </tfoot>
    );
  };

  const columns = [
    {
      field: "status",
      headerName: t("Status"),
    },
    { field: "count", headerName: t("No. of Case") },
    {
      field: "yearPremium",
      headerName: t("FY Premium"),
      cellRender: (col, data) => {
        return <Text>{formatter.currency(data.yearPremium)}</Text>;
      },
    },
    {
      field: "yearCommission",
      headerName: t("FY Commission (Total)"),
      cellRender: (col, data) => {
        return <Text>{formatter.currency(data.yearCommission ?? 0)}</Text>;
      },
    },
    {
      field: "yearConsultantCommission",
      headerName: t("FY Commission (Producers)"),
      cellRender: (col, data) => {
        return <Text>{formatter.currency(data.yearConsultantCommission ?? 0)}</Text>;
      },
    },
    // {
    //   field: "individualConsultantCommission",
    //   headerName: t("FY Commission (Individual)"),
    //   cellRender: (col, data) => {
    //     return (
    //       <Text>{formatter.currency(data.individualConsultantCommission)}</Text>
    //     );
    //   },
    // },
  ];

  return (
    <>
      {/* <ReactJson
        src={data}
        name="result"
        collapsed
        style={{ background: "white" }}
      /> */}

      <Card withBorder mb="md" mt="md" minHeight={1000} pos="relative">
        <SimpleLoadingIndicator visible={loading} />
        {/* <DataTable
          data={data}
          columns={columns}
          hidePagination={true}
          hideSearchBar={true}
          padding="xs"
          tdPaddingX={"2px"}
        /> */}

        <OrderFilter
          by={periodBy}
          handleRefresh={onPeriodRefresh}
          refreshLoading={loading}
          handlePeriodChange={onPeriodChange}
          handleUserChange={onUserChange}
          numPeriods={numQuarters}
          position="left"
          showArrow={false}
          iconButton={true}
          showPeriodOptionsSelector={true}
          withStaffFilter={true}
          withProductCategoryFilter={withProductCategoryFilter}
          onProductCategoryChange={onProductCategoryChange}
          specificUser={showIndividualConsultant ? auth()._id : null}
          onDateTypeChange={onDateTypeChange}
        />

        <Space h={"xs"} />
        <Paper pt="md">
          <Table>
            <TableHeader />
            <TableRows data={data} />
            <TableFooter data={data} />
          </Table>
          <Space h={"md"} />
        </Paper>
      </Card>

      {!_.isEmpty(remark) && (
        <Group noWrap mt="xl">
          <IconAlertTriangle size={12} />
          <Text size="xs">{remark}</Text>
        </Group>
      )}
    </>
  );
};

export default OrderStatsTable;
